/**
 * Site-level access code for the admin workspace.
 * This is a friction gate in front of login — not a replacement for staff auth.
 *
 * The primary code is read from VITE_ADMIN_ACCESS_CODE and is NEVER committed to the
 * repo (a hardcoded code was removed here — see #37 follow-up). It still
 * ships in the browser bundle like any VITE_ var, so it is not a secret;
 * it is rotatable per environment without a code change.
 *
 * Optional additional codes come from VITE_ADMIN_ADDITIONAL_ACCESS_CODES, a
 * strict JSON string array. These codes do not identify or authorize staff.
 * Fail closed when no valid code is configured.
 */
export const ADMIN_ACCESS_STORAGE_KEY = 'pazportal.admin.access-granted';

/** Configured code, or null when the env var is unset/empty. Safe under plain Node (no import.meta.env). */
export function getConfiguredAdminAccessCode(): string | null {
  const raw = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_ADMIN_ACCESS_CODE;
  const v = raw?.trim();
  return v ? v : null;
}

/** Parameters are injectable for tests; runtime defaults read only Vite config. */
export function getConfiguredAdminAccessCodes(
  primary: string | null = getConfiguredAdminAccessCode(),
  additional: string | undefined = (import.meta as { env?: Record<string, string | undefined> }).env?.VITE_ADMIN_ADDITIONAL_ACCESS_CODES,
): string[] {
  const codes = new Set<string>();
  const first = primary?.trim();
  if (first) codes.add(first);
  if (additional?.trim()) {
    try {
      const parsed: unknown = JSON.parse(additional);
      // Reject the entire additional configuration rather than accepting a
      // valid-looking subset of a malformed or mixed-type array.
      if (Array.isArray(parsed) && parsed.every((code) => typeof code === 'string')) {
        for (const code of parsed) {
          const value = code.trim();
          if (value) codes.add(value);
        }
      }
    } catch {
      // Invalid optional configuration must never disable the primary code or log values.
    }
  }
  return [...codes];
}

export function isAdminAccessCodeConfigured(): boolean {
  return getConfiguredAdminAccessCodes().length > 0;
}

export function normalizeAccessCode(input: string): string {
  return input.trim();
}

/** `expected` is injectable for tests; a single explicit code remains supported. */
export function isValidAdminAccessCode(
  input: string,
  expected: string | readonly string[] | null = getConfiguredAdminAccessCodes(),
): boolean {
  if (!expected) return false;
  const normalized = normalizeAccessCode(input);
  if (!normalized) return false;
  const codes = typeof expected === 'string' ? [expected] : expected;
  return codes.some((code) => normalized === code);
}

export function readAdminAccessUnlock(): boolean {
  try {
    return sessionStorage.getItem(ADMIN_ACCESS_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function persistAdminAccessUnlock(): void {
  try {
    sessionStorage.setItem(ADMIN_ACCESS_STORAGE_KEY, '1');
  } catch {
    // Private mode / storage blocked — unlock still holds for this render.
  }
}
